import { endpoints } from "@/configs/Settings";
import { useInterventionsContext } from "@/contexts/InterventionsContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetAdminQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetAdminQuestionnaireResponse";

import { PDFViewer } from "@react-pdf/renderer";
import base64 from "base-64";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useMemo, useRef, useState } from "react";
import * as uuid from "uuid";
import { PDFRender } from "./PDFRender";
import { Divider, Stack } from "@mui/material";
import {
  buildSncrConsultaUrl,
  useSncrPreviewOnSpecials,
} from "src-next/modules/sncr";

interface IProps {
  setBase64?: any;
  dataPdf: any;
  refact?: any;
}

/** Gera idValue no mesmo formato legado (timestamp + 4 dígitos). */
function createIdValue() {
  return (
    Date.now() +
    Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, "0")
  );
}

type StableDocIds = { uuid: string; idValue: string };

/** Um par por paciente + documento, vivo entre re-render e remount da mesma aba. */
const docIdsByScope = new Map<string, StableDocIds>();

function scopeKey(patientKey: string, docKey: string) {
  return `${patientKey}::${docKey}`;
}

function getOrCreateDocIds(patientKey: string, docKey: string): StableDocIds {
  const key = scopeKey(patientKey, docKey);
  const existing = docIdsByScope.get(key);
  if (existing) return existing;
  const created = { uuid: uuid.v1(), idValue: createIdValue() };
  docIdsByScope.set(key, created);
  return created;
}

export function releaseDocIds(patientKey?: string, docKey?: string) {
  if (!patientKey || !docKey) return;
  docIdsByScope.delete(scopeKey(patientKey, docKey));
}

function idValueToString(idValue: unknown): string {
  if (idValue === undefined || idValue === null) return "";
  return String(idValue);
}

/** Procura a chave no array inteiro, não só em [0]. */
export function readDocSlot(base64List: any, docKey: string) {
  if (!docKey || !Array.isArray(base64List)) return undefined;
  for (const item of base64List) {
    const slot = item?.[docKey];
    if (!slot) continue;
    return {
      key: docKey,
      uuid: slot.uuid as string | undefined,
      idValue: idValueToString(slot.idValue),
    };
  }
  return undefined;
}

/** Primeira chave do array que passar no predicado. */
export function readFirstDocSlot(
  base64List: any,
  match: (key: string) => boolean,
) {
  if (!Array.isArray(base64List) || typeof match !== "function") return undefined;
  for (const item of base64List) {
    if (!item || typeof item !== "object") continue;
    for (const key of Object.keys(item)) {
      if (!match(key) || !item[key]) continue;
      return readDocSlot(base64List, key);
    }
  }
  return undefined;
}

export function DocumentVisualization({ setBase64, dataPdf, refact }: IProps) {
  const [blob, setBlob] = useState<any>();
  const { selectedInterventions } = useInterventionsContext();
  const { userPatient } = useSessionContext();
  const [dataRegister, setDataRegister] = useState<any[]>([]);
  const [loading, setLoading] = useState(true); // Novo estado de loading
  const { organization } = useOrganizationContext();

  useSncrPreviewOnSpecials(!refact);

  // Trava o primeiro CPF não vazio — a chave do mapa não pode mudar no meio do PDF
  const patientScopeRef = useRef("");
  const incomingPatientCpf = dataPdf?.[0]?.patientCPF || "";
  if (!patientScopeRef.current && incomingPatientCpf) {
    patientScopeRef.current = incomingPatientCpf;
  }
  const patientKey =
    patientScopeRef.current || incomingPatientCpf || "sem-paciente";

  // Evita setBase64 repetido quando o blob por tipo não mudou
  const lastConvertedBlobRef = useRef<Record<string, any> | null>(null);
  // Blob mais recente — conversão async (FileReader) de blob antigo não pode sobrescrever base64
  const currentBlobRef = useRef<Record<string, any> | undefined>(undefined);

  const getStableDocIds = (key: string) => getOrCreateDocIds(patientKey, key);

  // Cópia local com organization — evita mutar dataPdf[0] no render
  const dataPdfWithOrg = useMemo(
    () => [{ ...(dataPdf?.[0] || {}), organization }],
    // deps por campos usados no PDF (pai costuma passar array inline a cada render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dataPdf?.[0]?.patientCPF,
      dataPdf?.[0]?.patientData,
      dataPdf?.[0]?.professionalData,
      dataPdf?.[0]?.location,
      organization,
    ],
  );

  // Separa os medicamentos simples por tipo para gerar documentos separados
  // (mesma separação da ListaDeIntervenções: medication | supplement | herbalMedicine)
  const medicationSimple = selectedInterventions.medicationSimple;
  const serviceList = selectedInterventions.service;
  const procedureList = selectedInterventions.procedure;
  const certificatesList = selectedInterventions.certificates;
  const medicationSpecial = selectedInterventions.medicationSpecial;

  const simpleMedicationData = useMemo(
    () =>
      medicationSimple?.filter(
        (e: any) =>
          e?.type === "medication" &&
          (refact ? e?.refact && !e?.sign : !e?.sign),
      ) || [],
    [medicationSimple, refact],
  );

  const simpleSupplementData = useMemo(
    () =>
      medicationSimple?.filter(
        (e: any) =>
          e?.type === "supplement" &&
          (refact ? e?.refact && !e?.sign : !e?.sign),
      ) || [],
    [medicationSimple, refact],
  );

  const simpleHerbalData = useMemo(
    () =>
      medicationSimple?.filter(
        (e: any) =>
          e?.type === "herbalMedicine" &&
          (refact ? e?.refact && !e?.sign : !e?.sign),
      ) || [],
    [medicationSimple, refact],
  );

  const examGeneralData = useMemo(
    () =>
      serviceList
        ?.filter(
          (el: any) =>
            !["363679005", "108252007"].includes(el?.category?.code),
        )
        ?.filter((e: any) => (refact ? e?.refact && !e?.sign : !e?.sign)) ||
      [],
    [serviceList, refact],
  );

  const procedureData = useMemo(
    () =>
      procedureList?.filter((e: any) =>
        refact ? e?.refact && !e?.sign : !e?.sign,
      ) || [],
    [procedureList, refact],
  );

  const labExams = useMemo(
    () =>
      serviceList
        ?.filter((el: any) => el?.category?.code === "108252007")
        ?.filter((e: any) => !e?.sign) || [],
    [serviceList],
  );

  const imageExams = useMemo(
    () =>
      serviceList
        ?.filter((el: any) => el?.category?.code === "363679005")
        ?.filter((e: any) => !e?.sign) || [],
    [serviceList],
  );

  const pendingCertificates = useMemo(
    () => certificatesList?.filter((e: any) => !e?.sign) || [],
    [certificatesList],
  );

  const pendingSpecial = useMemo(
    () =>
      medicationSpecial?.filter((e: any) => {
        if (refact) {
          return e?.refact && !e?.sign; // Filtra os itens que têm 'refact' e que não têm 'sign'
        }
        return !e?.sign;
      }) || [],
    [medicationSpecial, refact],
  );

  /** Campos que afetam o PDF — edição de nome/form/note deve invalidar o blob. */
  const pdfItemKey = (el: any) =>
    [
      el?.medicationRequestId || el?.serviceRequestId || el?.documentReferenceId || "",
      el?.type || "",
      el?.name || "",
      el?.label || "",
      el?.form || "",
      el?.note || "",
      el?.tag || "",
      el?.sncrNumero || "",
      el?.sncrTipo || "",
      el?.receituarioModelo || "",
      el?.pdfTitle || "",
      el?.category?.code || "",
      // CID do exame aparece no PDF; sem ele, editar só a condição não regenerava o blob
      el?.condition?.code || el?.condition?.value || "",
      el?.sign ? "1" : "0",
      el?.refact ? "1" : "0",
    ].join("|");

  // Quando o conteúdo das intervenções muda (não só a quantidade), invalida o blob
  const contentFingerprint = useMemo(
    () =>
      JSON.stringify({
        refact: !!refact,
        simpleMed: simpleMedicationData.map(pdfItemKey),
        simpleSup: simpleSupplementData.map(pdfItemKey),
        simpleHerb: simpleHerbalData.map(pdfItemKey),
        exam: examGeneralData.map(pdfItemKey),
        procedure: procedureData.map(pdfItemKey),
        lab: labExams.map(pdfItemKey),
        image: imageExams.map(pdfItemKey),
        cert: pendingCertificates.map(pdfItemKey),
        special: pendingSpecial.map(pdfItemKey),
      }),
    [
      refact,
      simpleMedicationData,
      simpleSupplementData,
      simpleHerbalData,
      examGeneralData,
      procedureData,
      labExams,
      imageExams,
      pendingCertificates,
      pendingSpecial,
    ],
  );

  // customPdf (fundo/logo) — se o blob nascer antes, o uuid trava o PDF sem customização
  const customPdfFingerprint = useMemo(
    () =>
      (dataRegister || [])
        .map((e: any) => e?.resource?.id || "")
        .filter(Boolean)
        .sort()
        .join("|"),
    [dataRegister],
  );

  useEffect(() => {
    lastConvertedBlobRef.current = null;
    setBlob(undefined);
    // Limpa base64 antigo (edição/remoção/customPdf) para não exibir PDF stale.
    // uuid/idValue ficam no mapa até o POST daquele documento.
    setBase64?.(undefined);
  }, [contentFingerprint, customPdfFingerprint, setBase64]);

  const pdfList = useMemo(() => {
    const entries = [
      { type: "simple-medication", data: simpleMedicationData },
      { type: "simple-supplement", data: simpleSupplementData },
      { type: "simple-herbalMedicine", data: simpleHerbalData },
      { type: "exam", data: examGeneralData },
      { type: "procedure", data: procedureData },
    ];
    return entries.map((entry) => {
      const ids = getStableDocIds(entry.type);
      return { ...entry, uuid: ids.uuid, idValue: ids.idValue };
    });
  }, [
    simpleMedicationData,
    simpleSupplementData,
    simpleHerbalData,
    examGeneralData,
    procedureData,
    patientKey,
  ]);

  const blobToBase64 = (blobValue: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blobValue);
      reader.onloadend = () => {
        const base64data = reader.result;
        resolve(base64data);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  useEffect(() => {
    // Atualiza antes do early return: reset (blob undefined) também invalida conversões em andamento
    currentBlobRef.current = blob;

    async function generateBase64() {
      if (!blob || loading || !setBase64) return;

      const keys = Object.keys(blob);
      const prev = lastConvertedBlobRef.current;
      // Skip se os blobs por tipo são os mesmos (evita cascata setBase64 → re-render)
      const sameAsLast =
        prev &&
        keys.length === Object.keys(prev).length &&
        keys.every((k) => prev[k]?.blob === blob[k]?.blob);

      if (sameAsLast) return;

      const convertToBase64 = keys.map(async (el: any) => {
        return {
          [el]: {
            base64: await blobToBase64(blob?.[el]?.blob),
            info: blob?.[el]?.info,
            type: el,
            uuid: blob?.[el]?.uuid,
            idValue: blob?.[el]?.idValue,
          },
        };
      });

      const base64Result = await Promise.all(convertToBase64);
      // Blob mudou durante a conversão (edição/reset) — descarta para não assinar PDF velho
      if (currentBlobRef.current !== blob) return;
      lastConvertedBlobRef.current = blob;
      setBase64(base64Result);
    }

    generateBase64();
    // Não chama setBase64(undefined) em loading/blob vazio — isso reiniciava o ciclo
  }, [blob, setBase64, loading]);

  useEffect(() => {
    async function getQuestionnaireResponse() {
      // Location do atendimento OU organização logada (remissão na agenda
      // pode abrir o PDF antes do Location estar resolvido)
      const orgAuthorId =
        dataPdf?.[0]?.location?.managingOrganization?.reference?.split(
          "/",
        )?.[1] || organization?.id;

      if (!orgAuthorId) {
        setDataRegister([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await GetAdminQuestionnaireResponse({
          questionnaire: "customPdf",
          author: orgAuthorId,
        });
        setDataRegister(response?.entry || []);
      } catch (err) {
        console.log("err", err);
        setDataRegister([]);
      } finally {
        setLoading(false);
      }
    }
    getQuestionnaireResponse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataPdf?.[0]?.location?.managingOrganization?.reference,
    organization?.id,
  ]);

  return (
    <Stack width="100%" spacing={2}>
      {!loading && (
        <>
          {pdfList
            ?.filter((e: any) => e?.data?.length)
            ?.map((el: any) => {
              const qrCodeEncodedValue = base64.encode(
                JSON.stringify({
                  organization: organization?.id?.split("-")?.[1],
                  patientId: dataPdfWithOrg?.[0]?.patientCPF,
                  uuid: el?.uuid,
                  idValue: el?.idValue,
                }),
              );

              const linkDocument = `${endpoints?.XDSB_domain}/portal/#/validateDocument/${qrCodeEncodedValue}`;
              return (
                <Stack width="100%" alignItems="center" key={el.type}>
                  {el?.uuid && (
                    <>
                      <div style={{ display: "none" }} id={`qrcode-${el.type}`}>
                        <QRCodeCanvas
                          value={linkDocument}
                          size={256}
                          bgColor="#ffffff"
                          fgColor="#000000"
                          level="L"
                        />
                      </div>

                      <PDFViewer
                        style={{ width: "1100px", height: "1150px" }}
                        showToolbar={false}
                      >
                        <PDFRender
                          linkDocument={linkDocument}
                          qrCodeSrc={`qrcode-${el.type}`}
                          uuid={el?.uuid}
                          dataPdf={dataPdfWithOrg}
                          idValue={el?.idValue}
                          content={el}
                          blob={blob}
                          setBlob={setBlob}
                          dataRegister={dataRegister}
                          gender={userPatient?.gender}
                        />
                      </PDFViewer>
                    </>
                  )}

                  <Divider
                    sx={{
                      width: "100%",
                      background: "neutral600.main",
                      paddingY: 1,
                    }}
                  />
                </Stack>
              );
            })}

          {/* Filtro para exames laboratoriais */}
          {labExams.map((el: any, index: number) => {
            const typeKey = `exam-${index}-lab`;
            const ids = getStableDocIds(typeKey);
            const auxContent = { type: typeKey, data: [el] };
            const uuidValue = ids.uuid;
            const examIdValue = ids.idValue;

            const qrCodeEncodedValue = base64.encode(
              JSON.stringify({
                organization: organization?.id?.split("-")?.[1],
                patientId: dataPdfWithOrg?.[0]?.patientCPF,
                uuid: uuidValue,
                idValue: examIdValue,
              }),
            );

            const linkDocument = `${endpoints?.XDSB_domain}/portal/#/validateDocument/${qrCodeEncodedValue}`;
            return (
              <Stack
                justifyContent="center"
                alignItems="center"
                key={typeKey}
              >
                <div style={{ display: "none" }} id={typeKey}>
                  <QRCodeCanvas
                    value={linkDocument}
                    size={256}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="L"
                  />
                </div>
                <PDFViewer
                  style={{ width: "1100px", height: "1150px" }}
                  showToolbar={false}
                >
                  <PDFRender
                    linkDocument={linkDocument}
                    qrCodeSrc={typeKey}
                    uuid={uuidValue}
                    idValue={examIdValue}
                    gender={userPatient?.gender}
                    index={index}
                    dataPdf={dataPdfWithOrg}
                    content={auxContent}
                    blob={blob}
                    setBlob={setBlob}
                    dataRegister={dataRegister}
                  />
                </PDFViewer>
                <Divider
                  sx={{
                    width: "100%",
                    background: "neutral600.main",
                    paddingY: 1,
                  }}
                />
              </Stack>
            );
          })}
          {/* Filtro para exames de imagem */}
          {imageExams.map((el: any, index: number) => {
            const typeKey = `exam-${index}-image`;
            const ids = getStableDocIds(typeKey);
            const auxContent = { type: typeKey, data: [el] };
            const uuidValue = ids.uuid;
            const examIdValue = ids.idValue;

            const qrCodeEncodedValue = base64.encode(
              JSON.stringify({
                organization: organization?.id?.split("-")?.[1],
                patientId: dataPdfWithOrg?.[0]?.patientCPF,
                uuid: uuidValue,
                idValue: examIdValue,
              }),
            );

            const linkDocument = `${endpoints?.XDSB_domain}/portal/#/validateDocument/${qrCodeEncodedValue}`;
            return (
              <Stack
                justifyContent="center"
                alignItems="center"
                key={typeKey}
              >
                <div style={{ display: "none" }} id={typeKey}>
                  <QRCodeCanvas
                    value={linkDocument}
                    size={256}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="L"
                  />
                </div>
                <PDFViewer
                  style={{ width: "1100px", height: "1150px" }}
                  showToolbar={false}
                >
                  <PDFRender
                    linkDocument={linkDocument}
                    qrCodeSrc={typeKey}
                    uuid={uuidValue}
                    idValue={examIdValue}
                    gender={userPatient?.gender}
                    index={index}
                    dataPdf={dataPdfWithOrg}
                    content={auxContent}
                    blob={blob}
                    setBlob={setBlob}
                    dataRegister={dataRegister}
                  />
                </PDFViewer>
                <Divider
                  sx={{
                    width: "100%",
                    background: "neutral600.main",
                    paddingY: 1,
                  }}
                />
              </Stack>
            );
          })}

          {pendingCertificates.map((el: any, index: number) => {
            const typeKey = `certificate-${index}`;
            const ids = getStableDocIds(typeKey);
            const auxContent = { type: typeKey, data: [el] };
            const uuidValue = ids.uuid;
            const certificateIdValue = ids.idValue;

            const qrCodeEncodedValue = base64.encode(
              JSON.stringify({
                organization: organization?.id?.split("-")?.[1],
                patientId: dataPdfWithOrg?.[0]?.patientCPF,
                uuid: uuidValue,
                idValue: certificateIdValue,
              }),
            );

            const linkDocument = `${endpoints?.XDSB_domain}/portal/#/validateDocument/${qrCodeEncodedValue}`;
            return (
              <Stack
                justifyContent="center"
                alignItems="center"
                key={typeKey}
              >
                <div style={{ display: "none" }} id={typeKey}>
                  <QRCodeCanvas
                    value={linkDocument}
                    size={256}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="L"
                  />
                </div>
                <PDFViewer
                  style={{ width: "1100px", height: "1150px" }}
                  showToolbar={false}
                >
                  <PDFRender
                    linkDocument={linkDocument}
                    qrCodeSrc={typeKey}
                    uuid={uuidValue}
                    idValue={certificateIdValue}
                    gender={userPatient?.gender}
                    index={index}
                    dataPdf={dataPdfWithOrg}
                    content={auxContent}
                    blob={blob}
                    setBlob={setBlob}
                    dataRegister={dataRegister}
                  />
                </PDFViewer>
                <Divider
                  sx={{
                    width: "100%",
                    background: "neutral600.main",
                    paddingY: 1,
                  }}
                />
              </Stack>
            );
          })}

          {pendingSpecial.map((el: any, index: number) => {
            const typeKey = `special-${index}`;
            const ids = getStableDocIds(typeKey);
            const auxContent = { type: typeKey, data: [el] };
            const uuidValue = ids.uuid;
            const specialIdValue = ids.idValue;

            const qrCodeEncodedValue = base64.encode(
              JSON.stringify({
                organization: organization?.id?.split("-")?.[1],
                patientId: dataPdfWithOrg?.[0]?.patientCPF,
                uuid: uuidValue,
                idValue: specialIdValue,
              }),
            );

            const linkDocument = `${endpoints?.XDSB_domain}/portal/#/validateDocument/${qrCodeEncodedValue}`;
            const sncrQrId = el?.sncrNumero
              ? `${typeKey}-sncr`
              : undefined;
            const sncrConsultaUrl = el?.sncrNumero
              ? buildSncrConsultaUrl(el.sncrNumero)
              : undefined;
            return (
              <Stack
                width="100%"
                alignItems="center"
                key={typeKey}
              >
                <div style={{ display: "none" }} id={typeKey}>
                  <QRCodeCanvas
                    value={linkDocument}
                    size={256}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="L"
                  />
                </div>
                {sncrQrId && sncrConsultaUrl ? (
                  <div style={{ display: "none" }} id={sncrQrId}>
                    <QRCodeCanvas
                      value={sncrConsultaUrl}
                      size={256}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="L"
                    />
                  </div>
                ) : null}
                <PDFViewer
                  style={{ width: "1100px", height: "1150px" }}
                  showToolbar={false}
                >
                  <PDFRender
                    linkDocument={linkDocument}
                    qrCodeSrc={typeKey}
                    qrCodeSncrSrc={sncrQrId}
                    uuid={uuidValue}
                    idValue={specialIdValue}
                    index={index}
                    dataPdf={dataPdfWithOrg}
                    content={auxContent}
                    blob={blob}
                    setBlob={setBlob}
                    dataRegister={dataRegister}
                    gender={userPatient?.gender}
                  />
                </PDFViewer>

                <Divider
                  sx={{
                    width: "100%",
                    background: "neutral600.main",
                    paddingY: 1,
                  }}
                />
              </Stack>
            );
          })}
        </>
      )}
    </Stack>
  );
}
